import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCachedFetch } from '../contexts/DataCacheContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Play, Pause, RotateCcw, Coffee, Brain, Target, Clock, CheckCircle2, Zap, Bell, BellOff, Waves, VolumeX, Music } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TIMER_PRESETS = {
  focus: { label: 'Focus', duration: 25, icon: Brain, color: 'violet' },
  short_break: { label: 'Short Break', duration: 5, icon: Coffee, color: 'emerald' },
  long_break: { label: 'Long Break', duration: 15, icon: Coffee, color: 'blue' },
};

export const FocusTimer = () => {
  const { api, refreshUser } = useAuth();
  const [mode, setMode] = useState('focus');
  const [timeLeft, setTimeLeft] = useState(TIMER_PRESETS.focus.duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [stats, setStats] = useState(null);
  const [sessionsCompleted, setSessionsCompleted] = useState(0);
  const [customDuration, setCustomDuration] = useState(25);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [ambientPlaying, setAmbientPlaying] = useState(false);
  const [isEditingTime, setIsEditingTime] = useState(false);
  const intervalRef = useRef(null);
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  const [resetConfirmMode, setResetConfirmMode] = useState(null);

  const [cachedStats, loading, refetchStats] = useCachedFetch('focusStats', async (signal) => {
    const response = await api.get('/focus/stats', { signal });
    return response.data;
  }, [api]);

  useEffect(() => {
    if (cachedStats) {
      setStats(cachedStats);
    }
  }, [cachedStats]);

  /**
   * Pomodoro State Machine:
   * Handles transitions between focus, short_break, and long_break modes.
   * Tracks completed sessions to trigger a long break every 4th pomodoro.
   * Also synchronizes the actual duration of the session with the backend.
   */
  const handleTimerComplete = useCallback(async () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);

    if (soundEnabled) {
      try {
        audioRef.current?.play();
      } catch {
      }
    }

    if (mode === 'focus' && currentSession) {
      try {
        await api.patch(`/focus/${currentSession.id}/complete`, {
          duration_actual: customDuration,
        });
        toast.success('Focus session complete! +25 XP 🎉', {
          icon: <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        });
        setSessionsCompleted(prev => prev + 1);
        refetchStats();
        refreshUser();
      } catch {
        toast.error('Failed to complete the focus session');
      }
      setCurrentSession(null);
    }

    if (mode === 'focus') {
      const nextMode = sessionsCompleted > 0 && (sessionsCompleted + 1) % 4 === 0 ? 'long_break' : 'short_break';
      setMode(nextMode);
      setTimeLeft(TIMER_PRESETS[nextMode].duration * 60);
      toast.info(`Time for a ${nextMode === 'long_break' ? 'long' : 'short'} break!`);
    } else {
      setMode('focus');
      setTimeLeft(TIMER_PRESETS.focus.duration * 60);
      toast.info('Break complete! Ready to focus?');
    }
  }, [mode, currentSession, sessionsCompleted, api, refetchStats, refreshUser, soundEnabled, customDuration]);

  useEffect(() => {
    audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
    ambientRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/125/125-preview.mp3');
    ambientRef.current.loop = true;
    ambientRef.current.volume = 0.4;
  }, []);

  useEffect(() => {
    if (ambientPlaying && isRunning) {
      ambientRef.current?.play().catch(() => setAmbientPlaying(false));
    } else {
      ambientRef.current?.pause();
    }
  }, [ambientPlaying, isRunning]);

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && isRunning) {
      handleTimerComplete();
    }

    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft, handleTimerComplete]);

  const startTimer = async () => {
    if (mode === 'focus' && !currentSession) {
      try {
        const response = await api.post('/focus/start', {
          duration_planned: customDuration
        });
        setCurrentSession(response.data);
      } catch (error) {
        toast.error('Failed to start session');
        return;
      }
    }
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);
  };

  const resetTimer = async () => {
    setIsRunning(false);
    clearInterval(intervalRef.current);

    if (currentSession && mode === 'focus') {
      // Mark as interrupted — no XP, no stats, no achievements
      try {
        await api.patch(`/focus/${currentSession.id}/complete`, {
          duration_actual: Math.max(0, Math.floor((totalDurationSeconds - timeLeft) / 60)),
          interrupted: true,
        });
      } catch (error) {
        if (error.response?.status !== 409) {
          toast.error('Failed to reset the active session');
          return false;
        }
      }
      refetchStats();
      refreshUser();
      setCurrentSession(null);
    }

    if (mode === 'focus' && customDuration !== TIMER_PRESETS.focus.duration) {
      setTimeLeft(customDuration * 60);
    } else {
      setTimeLeft(TIMER_PRESETS[mode].duration * 60);
    }
    return true;
  };

  const changeMode = (newMode) => {
    if (newMode === mode) return;
    // If there's an active focus session, ask for confirmation instead of abandoning it
    if (currentSession && mode === 'focus') {
      setResetConfirmMode(newMode);
      return;
    }
    setMode(newMode);
    if (isRunning) pauseTimer();
    if (newMode === 'focus' && customDuration !== TIMER_PRESETS.focus.duration) {
      setTimeLeft(customDuration * 60);
    } else {
      setTimeLeft(TIMER_PRESETS[newMode].duration * 60);
    }
  };

  const confirmModeSwitch = async () => {
    if (!resetConfirmMode) return;
    const resetSucceeded = await resetTimer(); // ends session as interrupted
    if (!resetSucceeded) {
      return;
    }
    const newMode = resetConfirmMode;
    setResetConfirmMode(null);
    setMode(newMode);
    setTimeLeft(TIMER_PRESETS[newMode].duration * 60);
  };

  const handleCustomDurationChange = (e) => {
    let val = parseInt(e.target.value);
    if (isNaN(val)) val = '';
    else if (val < 1) val = 1;
    else if (val > 180) val = 180; // max 3 hours
    setCustomDuration(val);

    if (val !== '') {
      if (!isRunning && mode === 'focus') {
        setTimeLeft(val * 60);
      }
    }
  };

  const handleDurationBlur = () => {
    setIsEditingTime(false);
    if (customDuration === '' || isNaN(customDuration)) {
      setCustomDuration(25);
      if (!isRunning && mode === 'focus') setTimeLeft(25 * 60);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const config = TIMER_PRESETS[mode];
  const totalDurationSeconds = mode === 'focus' && customDuration !== config.duration ? customDuration * 60 : config.duration * 60;
  const progress = ((totalDurationSeconds - timeLeft) / totalDurationSeconds) * 100;
  const Icon = config.icon;

  if (!stats && loading) {
    return (
      <div className="space-y-4 animate-in fade-in duration-300">
        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="w-full lg:flex-1">
            <div className="rounded-2xl bg-card shadow-neu-sm p-10 h-[400px] flex flex-col items-center justify-center space-y-6">
               <div className="w-48 h-8 rounded-lg bg-primary/10 animate-pulse" />
               <div className="w-48 h-48 rounded-full bg-primary/5 animate-pulse" />
               <div className="w-64 h-12 rounded-xl bg-primary/10 animate-pulse" />
            </div>
          </div>
          <div className="w-full lg:w-80 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="rounded-2xl bg-card shadow-neu-sm p-6 space-y-2">
                <div className="h-4 w-24 rounded-md bg-primary/10 animate-pulse" />
                <div className="h-6 w-16 rounded-lg bg-primary/10 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-card shadow-neu-sm p-6 space-y-4">
          <div className="h-6 w-64 rounded-lg bg-primary/10 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map(i => (
               <div key={i} className="flex gap-3">
                 <div className="h-6 w-6 rounded-full bg-primary/10 animate-pulse shrink-0" />
                 <div className="h-4 flex-1 rounded-md bg-primary/5 animate-pulse" />
               </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="focus-page">
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Timer */}
        <div className="w-full lg:flex-1">
          <Card className={`w-full bg-gradient-to-br from-card to-${config.color}-950/10`} data-testid="timer-card">
            <CardContent className="p-10">
              {/* Mode Selector */}
              <div className="flex justify-center gap-2 mb-6">
                {Object.entries(TIMER_PRESETS).map(([key, preset]) => (
                  <Button
                    key={key}
                    variant={mode === key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => changeMode(key)}
                    className={
                      mode === key
                        ? key === 'focus'
                          ? 'bg-violet-600 hover:bg-violet-700'
                          : key === 'short_break'
                            ? 'bg-emerald-600 hover:bg-emerald-700'
                            : 'bg-blue-600 hover:bg-blue-700'
                        : ''
                    }
                    data-testid={`mode-${key}-btn`}
                  >
                    <preset.icon className="w-4 h-4 mr-2" />
                    {preset.label}
                  </Button>
                ))}
              </div>

              {/* Session reset confirmation prompt */}
              {resetConfirmMode && (
                <div className="mb-4 rounded-2xl bg-amber-500/10 px-4 py-3 flex flex-col gap-2 shadow-neu-sm">
                  <p className="text-sm font-medium text-amber-400">You have an active focus session. Reset it to switch mode?</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={confirmModeSwitch}>Reset &amp; Switch</Button>
                    <Button size="sm" variant="outline" onClick={() => setResetConfirmMode(null)}>Keep Running</Button>
                  </div>
                </div>
              )}

              {/* Timer Display */}
              <div className="relative w-48 h-48 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="88"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="6"
                    className="text-muted/30"
                  />
                  <motion.circle
                    cx="96"
                    cy="96"
                    r="88"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="6"
                    strokeLinecap="round"
                    className={`text-${config.color}-500`}
                    strokeDasharray={2 * Math.PI * 88}
                    strokeDashoffset={2 * Math.PI * 88 * (1 - progress / 100)}
                    initial={false}
                    animate={{ strokeDashoffset: 2 * Math.PI * 88 * (1 - progress / 100) }}
                    transition={{ duration: 0.5 }}
                  />
                </svg>

                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.div
                    key={timeLeft}
                    initial={{ scale: 1.1, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="text-4xl font-bold font-mono"
                    data-testid="timer-display"
                  >
                    {!isRunning && mode === 'focus' && isEditingTime ? (
                      <input
                        type="number"
                        value={customDuration}
                        onChange={handleCustomDurationChange}
                        onBlur={handleDurationBlur}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleDurationBlur(); }}
                        autoFocus
                        min="1" max="180"
                        className="w-24 bg-transparent border-none text-center focus:outline-none placeholder-muted-foreground/30"
                        placeholder="25"
                      />
                    ) : (
                      <div
                        onClick={() => { if (!isRunning && mode === 'focus') setIsEditingTime(true); }}
                        className={!isRunning && mode === 'focus' ? 'cursor-text hover:text-muted-foreground transition-colors' : ''}
                        title={!isRunning && mode === 'focus' ? "Click to edit duration" : ""}
                      >
                        {formatTime(timeLeft)}
                      </div>
                    )}
                  </motion.div>
                  <Badge variant="outline" className={`mt-2 bg-${config.color}-500/20 text-${config.color}-400`}>
                    <Icon className="w-3 h-3 mr-1" />
                    {config.label}
                  </Badge>
                </div>
              </div>

              {/* Sound Settings & Spotify */}
              <div className="flex justify-center gap-2 mb-6 text-muted-foreground">
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/50 hover:text-foreground" onClick={() => setSoundEnabled(!soundEnabled)} title="Toggle completion sound">
                  {soundEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4 opacity-50" />}
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-muted/50 hover:text-foreground" onClick={() => setAmbientPlaying(!ambientPlaying)} title="Toggle ambient rain sound">
                  {ambientPlaying ? <Waves className="w-4 h-4 text-blue-400" /> : <VolumeX className="w-4 h-4 opacity-50" />}
                </Button>
                <a href="https://open.spotify.com" target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-green-500/20 hover:text-green-500 transition-colors" title="Open Deep Focus on Spotify">
                    <Music className="w-4 h-4" />
                  </Button>
                </a>
              </div>

              {/* Controls */}
              <div className="flex justify-center gap-4">
                {!isRunning ? (
                  <Button
                    size="lg"
                    onClick={startTimer}
                    className={`gap-2 shadow-neu ${mode === 'focus'
                      ? 'bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800'
                      : mode === 'short_break'
                        ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                      }`}
                    data-testid="start-btn"
                  >
                    <Play className="w-5 h-5" /> Start
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    onClick={pauseTimer}
                    variant="outline"
                    className="gap-2"
                    data-testid="pause-btn"
                  >
                    <Pause className="w-5 h-5" /> Pause
                  </Button>
                )}

                <Button
                  size="lg"
                  variant="ghost"
                  onClick={resetTimer}
                  className="gap-2"
                  data-testid="reset-btn"
                >
                  <RotateCcw className="w-5 h-5" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stats */}
        <div className="w-full lg:w-80 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card data-testid="today-focus-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-s text-muted-foreground">Today</p>
                    <p className="text-lg font-bold font-mono mt-0.5">
                      {Math.floor((stats?.today_focus_time || 0) / 60)}h {(stats?.today_focus_time || 0) % 60}m
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-violet-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card data-testid="sessions-today-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-s text-muted-foreground">Sessions Today</p>
                    <p className="text-lg font-bold font-mono mt-0.5">{stats?.today_sessions || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-emerald-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card data-testid="total-focus-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-s text-muted-foreground">Total Focus</p>
                    <p className="text-lg font-bold font-mono mt-0.5">
                      {Math.floor((stats?.total_focus_time || 0) / 60)}h {(stats?.total_focus_time || 0) % 60}m
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card data-testid="completion-rate-stat">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-s text-muted-foreground">Total Sessions</p>
                    <p className="text-lg font-bold font-mono mt-0.5">
                      {stats?.total_sessions || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Pomodoro Technique Info */}
      <Card className="">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-violet-500" />
            How to use the Pomodoro Technique
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-violet-500">1</span>
              </div>
              <p className="text-muted-foreground">Choose a task to work on</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-violet-500">2</span>
              </div>
              <p className="text-muted-foreground">Work for 25 minutes (one "pomodoro")</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-violet-500">3</span>
              </div>
              <p className="text-muted-foreground">Take a 5-minute break</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-violet-500">4</span>
              </div>
              <p className="text-muted-foreground">After 4 pomodoros, take a longer break (15-30 min)</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div >
  );
};
